import { canonicalize } from "./canonical.js";
import { isHex64, sha256Hex, signHex, verifySig, hexToBytes, utf8ToBytes } from "./crypto.js";
import type { Identity } from "./crypto.js";

export const OP_VERSION = 1;

export type OpKind =
  | "genesis"
  | "member_add"
  | "member_leave"
  | "member_remove"
  | "ghost_add"
  | "ghost_claim"
  | "expense"
  | "payment"
  | "payment_ack"
  | "supersede"
  | "profile"
  | "key_rotate";

// All money amounts are integer minor units (paise). Enforced by construction:
// canonicalize() rejects non-integers, validate() rejects non-positive amounts.

export interface SplitEqual {
  type: "equal";
  participants: string[]; // member pubkeys
}
export interface SplitExact {
  type: "exact";
  participants: string[];
  amounts: number[]; // paise, index-aligned with participants, must sum to expense amount
}
export type Split = SplitEqual | SplitExact;

export interface GenesisBody {
  name: string;
  currency: string; // ISO 4217, e.g. "INR"
}
export interface MemberAddBody {
  member: string; // pubkey being added
  name: string; // display name as known by the adder
}
export interface MemberLeaveBody {
  member: string; // must equal op.author (leaving is always your own act)
}
// Eviction. Any active member may remove any OTHER active member: the group has
// no admin role, and inventing one would need a succession story for when the
// creator leaves or loses their device. What makes it safe is accountability
// rather than prevention - the op is signed, permanent, and names who did it.
// Removal is a set deletion, so two members removing each other concurrently
// both apply and both end up out, in any ingest order.
export interface MemberRemoveBody {
  member: string; // must NOT equal op.author (that is member_leave)
  reason: string; // free text, may be "", shown in the ledger
}
// Changing the group key. The old key stays valid for everything already
// written: rotation protects FUTURE entries only, and the app says so. The new
// key is wrapped once per remaining member, to an X25519 key derived from the
// Ed25519 identity that is already their member id, so nobody has to publish
// anything first.
export interface KeyRotateBody {
  epoch: number; // exactly one past the highest epoch among this op's ancestors
  eph: string; // 64-hex X25519 ephemeral public key for this rotation
  keys: WrappedKey[]; // one per active member at this op's parents, sorted by member
}
export interface WrappedKey {
  m: string; // member pubkey
  k: string; // base64url AES-GCM(nonce ++ ciphertext) of the new 32-byte group key
}
// A ghost is a person tracked by name only: no device, no keypair. Ghosts can
// owe, be owed, and pay, but never author ops. The ghost id has pubkey shape
// (32 random bytes, hex) so every id-keyed code path treats it uniformly.
export interface GhostAddBody {
  ghost: string; // 64-hex random id, NOT a real pubkey
  name: string;
}
// When the person finally installs, they claim their ghost onto their real
// key. Self-claim only: nobody can redirect a ghost's debt onto someone else.
export interface GhostClaimBody {
  ghost: string;
  member: string; // must equal op.author
}
export interface ExpenseBody {
  desc: string;
  amount: number; // paise
  paidBy: string; // member pubkey
  split: Split;
}
export interface PaymentBody {
  from: string;
  to: string;
  amount: number; // paise
  note: string;
}
// The receiver's signed confirmation of a payment: the second tick.
// Authorization pins to the ORIGINAL payment body's `to`; if the payment is
// later edited to a different receiver, an existing ack goes stale (accepted
// v1 tradeoff, edits of settled payments are already a social anomaly).
export interface PaymentAckBody {
  target: string; // hash of the payment op being confirmed
}
export interface SupersedeBody {
  target: string; // hash of the expense/payment op being edited or voided
  replacement: ExpenseBody | PaymentBody | null; // null = void ("delete")
}
export interface ProfileBody {
  name: string;
  vpa: string; // UPI id, "" if unset
}

export type OpBody =
  | GenesisBody
  | MemberAddBody
  | MemberLeaveBody
  | MemberRemoveBody
  | KeyRotateBody
  | GhostAddBody
  | GhostClaimBody
  | ExpenseBody
  | PaymentBody
  | PaymentAckBody
  | SupersedeBody
  | ProfileBody;

export interface Op {
  v: number;
  group: string; // genesis op hash; "" on the genesis op itself
  author: string; // pubkey hex
  parents: string[]; // DAG head hashes at creation time (hash-linking)
  lamport: number; // > max(parents.lamport); 0 for genesis
  ts: number; // unix ms, advisory only - never used for ordering or validity
  kind: OpKind;
  body: OpBody;
  hash: string; // sha256 over canonical serialization of the fields above
  sig: string; // ed25519(author, hash-bytes)
}

export function computeOpHash(op: Omit<Op, "hash" | "sig">): string {
  return sha256Hex(
    canonicalize({
      v: op.v,
      group: op.group,
      author: op.author,
      parents: op.parents,
      lamport: op.lamport,
      ts: op.ts,
      kind: op.kind,
      body: op.body as never,
    }),
  );
}

export interface BuildOpInput {
  group: string;
  parents: string[];
  lamport: number;
  ts: number;
  kind: OpKind;
  body: OpBody;
}

export function buildOp(identity: Identity, input: BuildOpInput): Op {
  const partial = {
    v: OP_VERSION,
    group: input.group,
    author: identity.pubKey,
    parents: [...input.parents].sort(),
    lamport: input.lamport,
    ts: input.ts,
    kind: input.kind,
    body: input.body,
  };
  const hash = computeOpHash(partial);
  const sig = signHex(identity.privKey, hexToBytes(hash));
  return { ...partial, hash, sig };
}

export function verifyOpIntegrity(op: Op): { ok: true } | { ok: false; reason: string } {
  let recomputed: string;
  try {
    recomputed = computeOpHash(op);
  } catch (e) {
    return { ok: false, reason: `uncanonical body: ${(e as Error).message}` };
  }
  if (recomputed !== op.hash) return { ok: false, reason: "hash mismatch" };
  if (!isHex64(op.author)) return { ok: false, reason: "bad author key format" };
  if (!verifySig(op.sig, hexToBytes(op.hash), op.author)) {
    return { ok: false, reason: "bad signature" };
  }
  return { ok: true };
}

// Guard against absurd payloads before they enter anyone's store.
export const LIMITS = {
  desc: 200,
  name: 64,
  note: 200,
  vpa: 64,
  participants: 64,
  parents: 32,
  reason: 200,
  // A rotation wraps the key once per member, so the op grows with the group.
  // Same ceiling as participants: past this, a group is not a dinner table.
  wrappedKeys: 64,
} as const;

// 12-byte nonce ++ 32-byte key ++ 16-byte GCM tag, base64url without padding.
export const WRAPPED_KEY_B64_LEN = 80;

export function utf8Len(s: string): number {
  return utf8ToBytes(s).length;
}
